/**
 * PrefabManager - manages prefab template loading and caching.
 */

import { projectInfo } from '../../storage/projectContext.js';
import { AssetPath } from '../../storage/storageProvider.js';

import type { BaseInfo, SerializedData } from './types.js';


export class MissingPrefabDataError extends Error {
  public prefabPath: string;
  constructor(prefabPath: string) {
    super(`Prefab data is missing for path: ${prefabPath}, use the async version to load from prefab name/path.`);
    this.prefabPath = prefabPath;
  }
}

export class PrefabManager {
  private static prefabCache: Map<string, SerializedData> = new Map();

  private static makeKeyFromPath(path: string | AssetPath): string {
    const assetPath = path instanceof AssetPath ? path : AssetPath.fromString(path);
    return assetPath.initialPath;
  }

  // clears all cached prefab data, for testing or reloading purposes
  public static clearCache(): void {
    this.prefabCache.clear();
  }

  public static getPrefabBaseInfo(path: string | AssetPath): BaseInfo | null {
    const prefabData = this.getPrefabData(path);
    const root = prefabData?.$root;
    if (root && typeof root === 'object' && !Array.isArray(root)) {
      return (root as { $base?: BaseInfo }).$base ?? null;
    }
    return null;
  }

  public static getPrefabData(path: string | AssetPath): SerializedData | null {
    try {
      return this.getPrefabDataChecked(path);
    }
    catch (error) {
      if (error instanceof MissingPrefabDataError) {
        return null;
      }
      throw error;
    }
  }

  public static getPrefabDataChecked(path: string | AssetPath): SerializedData {
    const key = this.makeKeyFromPath(path);
    if (this.prefabCache.has(key)) {
      return this.prefabCache.get(key)!;
    }
    throw new MissingPrefabDataError(key);
  }

  /**
   * Cache prefab JSON for synchronous loading. Values are full serialized documents (`SerializedData`)
   * matching on-disk prefab / serialized actor shape.
   */
  public static setPrefabData(path: string | AssetPath, data: SerializedData | undefined): void {
    const key = this.makeKeyFromPath(path);
    if (data === undefined) {
      this.prefabCache.delete(key);
      return;
    }
    this.prefabCache.set(key, data);
  }

  public static async getOrLoadPrefabData(path: string | AssetPath): Promise<SerializedData> {
    const cachedData = this.getPrefabData(path);
    if (cachedData) {
      return cachedData;
    }
    const assetPath = path instanceof AssetPath ? path : AssetPath.fromString(path);

    const storageProvider = projectInfo.storageProvider;
    const data: SerializedData = await storageProvider.downloadFileAsJson(assetPath);

    this.setPrefabData(assetPath, data);
    return data;
  }
}
